// DuaJournalTab — personal dua tracker ported from Apps/Yawmi/DuaJournal.ts (175 LOC).
// Storage: aweni_yawmi_duas. Boundary: this is the PERSONAL JOURNAL (writing your own
// duas + tracking which were answered). The collection of canonical situational duas
// lives in Hisni. No Islamic content text is hardcoded here — user types their own.
import type { AweniContext } from '../../types';

interface Dua {
  id: string;
  text: string;
  category: string;          // user-chosen tag
  createdAt: string;         // ISO
  pinned: boolean;
  answered: boolean;
  answeredAt?: string;       // ISO when marked
  notes?: string;
}

const DUAS_KEY = 'aweni_yawmi_duas';
const LEGACY_KEY = 'yw_duas';
const MIGRATION_FLAG = 'aweni_yawmi_yw_migration_done';

const CATEGORIES = ['general', 'family', 'health', 'work', 'guidance', 'forgiveness', 'gratitude'];

export class DuaJournalTab {
  private el: HTMLElement;
  private duas: Dua[] = [];
  private filter: 'all' | 'active' | 'answered' | 'pinned' = 'active';
  private adding = false;

  constructor(container: HTMLElement) { this.el = container; }

  mount(ctx: AweniContext): void {
    this.migrate();
    try { this.duas = JSON.parse(localStorage.getItem(DUAS_KEY) || '[]'); } catch { this.duas = []; }
    this.render(ctx);
  }

  unmount(): void { this.el.innerHTML = ''; }

  private migrate(): void {
    // One-shot migration of yw_duas → aweni_yawmi_duas
    const flag = localStorage.getItem(MIGRATION_FLAG) || '';
    if (flag.includes('duas')) return;
    try {
      const legacy = localStorage.getItem(LEGACY_KEY);
      if (legacy && !localStorage.getItem(DUAS_KEY)) {
        localStorage.setItem(DUAS_KEY, legacy);
      }
    } catch { /* best-effort */ }
    localStorage.setItem(MIGRATION_FLAG, (flag ? flag + ',' : '') + 'duas');
  }

  private save(): void { localStorage.setItem(DUAS_KEY, JSON.stringify(this.duas)); }
  private tr(ctx: AweniContext, k: string, fb: string): string { return ctx.i18n.t(k) || fb; }

  private filtered(): Dua[] {
    let out = this.duas;
    if (this.filter === 'active') out = out.filter(d => !d.answered);
    else if (this.filter === 'answered') out = out.filter(d => d.answered);
    else if (this.filter === 'pinned') out = out.filter(d => d.pinned);
    return [...out].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return b.createdAt.localeCompare(a.createdAt);
    });
  }

  private addDua(text: string, category: string): void {
    if (!text.trim()) return;
    this.duas.unshift({
      id: 'd_' + Math.random().toString(36).slice(2, 10),
      text: text.trim(),
      category,
      createdAt: new Date().toISOString(),
      pinned: false,
      answered: false,
    });
    this.save();
  }

  private togglePin(id: string): void {
    const d = this.duas.find(x => x.id === id);
    if (d) { d.pinned = !d.pinned; this.save(); }
  }

  private toggleAnswered(id: string): void {
    const d = this.duas.find(x => x.id === id);
    if (!d) return;
    d.answered = !d.answered;
    d.answeredAt = d.answered ? new Date().toISOString() : undefined;
    this.save();
  }

  private deleteDua(id: string): void {
    this.duas = this.duas.filter(x => x.id !== id);
    this.save();
  }

  private render(ctx: AweniContext): void {
    const t = (k: string, fb: string) => this.tr(ctx, k, fb);
    const locale = ctx.i18n.locale || 'en';
    const items = this.filtered();
    const counts = {
      total: this.duas.length,
      answered: this.duas.filter(d => d.answered).length,
      pinned: this.duas.filter(d => d.pinned).length,
    };

    const formatDate = (iso: string) => {
      try { return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso)); }
      catch { return new Date(iso).toDateString(); }
    };

    this.el.innerHTML = `
      <style>
        .dj-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:12px}
        .dj-stat{background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,.08));
          border-radius:var(--ln-radius-md,12px);padding:8px;text-align:center}
        .dj-stat-n{font-size:1.2rem;font-weight:800;color:var(--ln-accent,#0A0A0A)}
        .dj-stat-l{font-size:.7rem;color:var(--ln-text-secondary);text-transform:uppercase;letter-spacing:.5px}
        .dj-filters{display:flex;gap:6px;margin-bottom:12px;overflow-x:auto;scrollbar-width:none}
        .dj-filters::-webkit-scrollbar{display:none}
        .dj-filter{flex-shrink:0;padding:6px 12px;border:1.5px solid var(--ln-border,rgba(0,0,0,.1));
          border-radius:var(--ln-radius-full,9999px);background:var(--ln-bg-card,#fff);
          color:var(--ln-text-secondary);font-size:.8rem;font-weight:700;cursor:pointer;white-space:nowrap}
        .dj-filter--active{background:var(--ln-accent);color:var(--ln-accent-text);border-color:var(--ln-accent)}
        .dj-add-btn{width:100%;padding:10px;margin-bottom:12px;background:var(--ln-accent);color:var(--ln-accent-text);
          border:none;border-radius:var(--ln-radius-md,12px);font-weight:700;cursor:pointer;font-size:.9rem}
        .dj-form{background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,.1));
          border-radius:var(--ln-radius-md,12px);padding:12px;margin-bottom:12px}
        .dj-form textarea{width:100%;min-height:80px;padding:8px;border:1px solid var(--ln-border,rgba(0,0,0,.1));
          border-radius:var(--ln-radius-sm,6px);font-family:inherit;font-size:.9rem;resize:vertical}
        .dj-form select{margin-top:8px;padding:6px;border:1px solid var(--ln-border,rgba(0,0,0,.1));
          border-radius:var(--ln-radius-sm,6px);font-size:.85rem;background:var(--ln-bg-card,#fff)}
        .dj-form-actions{display:flex;gap:6px;margin-top:8px;justify-content:flex-end}
        .dj-btn{padding:6px 12px;border:none;border-radius:var(--ln-radius-sm,6px);cursor:pointer;font-weight:600;font-size:.85rem}
        .dj-btn--primary{background:var(--ln-accent);color:var(--ln-accent-text)}
        .dj-btn--secondary{background:transparent;color:var(--ln-text-secondary);border:1px solid var(--ln-border)}
        .dj-list{display:flex;flex-direction:column;gap:8px}
        .dj-card{background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,.08));
          border-radius:var(--ln-radius-md,12px);padding:10px 12px;position:relative}
        .dj-card--answered{opacity:.6;border-style:dashed}
        .dj-card--pinned{border-color:var(--ln-accent);border-width:1.5px}
        .dj-text{font-size:.95rem;line-height:1.45;color:var(--ln-text-primary,#222);white-space:pre-wrap}
        .dj-meta{margin-top:6px;display:flex;gap:8px;align-items:center;font-size:.7rem;color:var(--ln-text-secondary);text-transform:uppercase;letter-spacing:.4px}
        .dj-cat{padding:2px 6px;background:var(--ln-bg-subtle,rgba(0,0,0,.05));border-radius:var(--ln-radius-sm,4px)}
        .dj-actions{margin-top:8px;display:flex;gap:6px}
        .dj-action{padding:4px 8px;background:transparent;border:1px solid var(--ln-border,rgba(0,0,0,.1));
          border-radius:var(--ln-radius-sm,4px);font-size:.72rem;color:var(--ln-text-secondary);cursor:pointer}
        .dj-action:hover{background:var(--ln-bg-subtle,rgba(0,0,0,.04))}
        .dj-action--danger{color:#b91c1c}
        .dj-empty{text-align:center;padding:32px 16px;color:var(--ln-text-secondary);font-size:.9rem}
      </style>

      <div class="dj-stats">
        <div class="dj-stat"><div class="dj-stat-n">${counts.total}</div><div class="dj-stat-l">${t('yawmi_duas_total','Total')}</div></div>
        <div class="dj-stat"><div class="dj-stat-n">${counts.answered}</div><div class="dj-stat-l">${t('yawmi_duas_answered','Answered')}</div></div>
        <div class="dj-stat"><div class="dj-stat-n">${counts.pinned}</div><div class="dj-stat-l">${t('yawmi_duas_pinned','Pinned')}</div></div>
      </div>

      <div class="dj-filters" role="tablist">
        ${(['active','all','answered','pinned'] as const).map(f => `
          <button class="dj-filter${f===this.filter?' dj-filter--active':''}" data-filter="${f}">
            ${escHtml(t('yawmi_duas_filter_'+f, f.charAt(0).toUpperCase()+f.slice(1)))}
          </button>`).join('')}
      </div>

      ${this.adding ? `
        <div class="dj-form">
          <textarea id="dj-text" placeholder="${escAttr(t('yawmi_duas_placeholder','Write your dua…'))}"></textarea>
          <select id="dj-category">
            ${CATEGORIES.map(c => `<option value="${c}">${escHtml(t('yawmi_duas_cat_'+c, c))}</option>`).join('')}
          </select>
          <div class="dj-form-actions">
            <button class="dj-btn dj-btn--secondary" id="dj-cancel">${escHtml(t('yawmi_duas_cancel','Cancel'))}</button>
            <button class="dj-btn dj-btn--primary" id="dj-save">${escHtml(t('yawmi_duas_save','Save'))}</button>
          </div>
        </div>
      ` : `
        <button class="dj-add-btn" id="dj-add">+ ${escHtml(t('yawmi_duas_add','Add dua'))}</button>
      `}

      <div class="dj-list" role="list">
        ${items.length === 0 ? `
          <div class="dj-empty">${escHtml(t('yawmi_duas_no_duas','No duas yet. Add your first.'))}</div>
        ` : items.map(d => `
          <div class="dj-card${d.answered?' dj-card--answered':''}${d.pinned?' dj-card--pinned':''}" role="listitem">
            <div class="dj-text">${escHtml(d.text)}</div>
            <div class="dj-meta">
              <span class="dj-cat">${escHtml(t('yawmi_duas_cat_'+d.category, d.category))}</span>
              <span>${escHtml(formatDate(d.createdAt))}</span>
              ${d.answered && d.answeredAt ? `<span>✓ ${escHtml(formatDate(d.answeredAt))}</span>` : ''}
            </div>
            <div class="dj-actions">
              <button class="dj-action" data-act="pin" data-id="${d.id}">
                ${d.pinned ? '📌' : '○'} ${escHtml(t('yawmi_duas_pin','Pin'))}
              </button>
              <button class="dj-action" data-act="answered" data-id="${d.id}">
                ${d.answered ? '↩' : '✓'} ${escHtml(d.answered ? t('yawmi_duas_unmark','Unmark') : t('yawmi_duas_mark_answered','Mark answered'))}
              </button>
              <button class="dj-action dj-action--danger" data-act="delete" data-id="${d.id}">
                ${escHtml(t('yawmi_duas_delete','Delete'))}
              </button>
            </div>
          </div>
        `).join('')}
      </div>`;

    // Filter buttons
    this.el.querySelectorAll<HTMLButtonElement>('.dj-filter').forEach(b => {
      b.addEventListener('click', () => {
        this.filter = b.dataset.filter as typeof this.filter;
        this.render(ctx);
      });
    });

    // Add button
    this.el.querySelector('#dj-add')?.addEventListener('click', () => {
      this.adding = true; this.render(ctx);
      setTimeout(() => (this.el.querySelector('#dj-text') as HTMLTextAreaElement)?.focus(), 50);
    });

    // Form save/cancel
    this.el.querySelector('#dj-save')?.addEventListener('click', () => {
      const txt = (this.el.querySelector('#dj-text') as HTMLTextAreaElement)?.value || '';
      const cat = (this.el.querySelector('#dj-category') as HTMLSelectElement)?.value || 'general';
      this.addDua(txt, cat);
      this.adding = false; this.render(ctx);
    });
    this.el.querySelector('#dj-cancel')?.addEventListener('click', () => {
      this.adding = false; this.render(ctx);
    });

    // Per-card actions
    this.el.querySelectorAll<HTMLButtonElement>('.dj-action').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.id!;
        const act = btn.dataset.act;
        if (act === 'pin') this.togglePin(id);
        else if (act === 'answered') this.toggleAnswered(id);
        else if (act === 'delete') {
          if (confirm(t('yawmi_duas_confirm_delete','Delete this dua?'))) this.deleteDua(id);
        }
        this.render(ctx);
      });
    });
  }
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
